import React, { useContext } from "react";
import { useIntl } from "react-intl";
import { Highlighter } from "react-bootstrap-typeahead";
import { useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import InputField from "components/atoms/InputField/InputField";
import Dropdown from "components/atoms/Dropdown/Dropdown";
import { ReactComponent as CircleCross } from "assets/images/icons/CircleCross.svg";
import SearchSelect from "pages/shipper/RequestForm/components/Common/SearchSelect";
import { ICurrencyOption } from "types/Common";
import { useAsyncDebounce } from "react-table";
import { quotationFormQueryKeys } from "api/queries/quotation-form/quotationFormQueryKeys";
import { getPackingListCalculations } from "helpers/quotationFormHelper";
import { formatPriceV2, sliceDecimalsWithoutRounding } from "helpers/commonHelpers";
import { COST_REGEX, handleOnKeyPressCost } from "helpers/costHelpers";
import { isValueExist } from "helpers/validationHelpers";
import CostFormCalculationContext from "./CostCalculationContext";

type CostFormChargeItemProps = {
  config: any;
  currencies: any;
  currentCustomChargeCode: any;
  remove: any;
  quoteDetail: any;
  chargeName: string;
  currentIndex: number;
  IsChargeableWeightRequired: boolean;
};

function CostFormChargeItem(props: CostFormChargeItemProps) {
  const {
    config,
    currencies,
    chargeName,
    currentIndex,
    remove,
    quoteDetail,
    currentCustomChargeCode,
    IsChargeableWeightRequired,
  } = props;
  const intl = useIntl();
  const { id: shipmentId } = useParams();
  const queryClient = useQueryClient();
  const { calculateTotalCost } = useContext<any>(CostFormCalculationContext);
  const { getValues, setValue } = useFormContext();
  const currentCharge = getValues(`costs.${chargeName}.${currentIndex}`);
  const cachedConfig: any = queryClient.getQueryData(quotationFormQueryKeys.config(shipmentId!));
  const isChargeCodeMandatory =
    currentCharge?.custom_charge_code_id && currentCustomChargeCode?.[currentCharge?.custom_charge_code_id]?.mandatory;

  const handleCurrencyRenderOption = ({ option, text }: { option: ICurrencyOption; text: string }) => (
    <div className="p-2">
      <div className="font-weight-bold font-12">
        <Highlighter search={text}>{`(${option.code}) ${option.name}`}</Highlighter>
      </div>
    </div>
  );

  const options = config?.measurement_units?.filter(
    (option: any) => IsChargeableWeightRequired || option.label !== "Per chargeable weight",
  );

  const recalculateAmount = useAsyncDebounce(() => {
    calculateTotalCost(chargeName);
  }, 500);

  const handleMeasurementUnitChange = (val: any) => {
    let quantity: any = 0;
    if (["Per shipment", "Per unit"].includes(val?.label)) {
      quantity = 1;
    } else if (val?.label === "Per cbm") {
      quantity = quoteDetail?.organization?.requested_cbm;
    } else if (val?.label === "Per weight") {
      const { totalWeight } = getPackingListCalculations(quoteDetail) ?? {};
      quantity = Number(totalWeight?.replace(/,/g, ""));
    } else if (val?.label === "Per chargeable weight") {
      quantity =
        getValues("service.freight_mode") === "road_freight"
          ? getValues()?.service?.rf_max_wt || ""
          : getValues()?.service?.af_chargeable_wt || "";
    } else if (/\b\d+ft\b/i.test(val?.label)) {
      const totalContainers = quoteDetail?.organization?.good_items?.reduce?.((acc: number, curr: any) => {
        if (curr?.handling_unit === "container") {
          return acc + curr.units;
        }
        return acc + 0;
      }, 0);
      quantity = totalContainers || "";
    }

    if (isNaN(quantity) || quantity === "") {
      quantity = 0;
    }
    setValue(`costs.${chargeName}.${currentIndex}.quantity`, sliceDecimalsWithoutRounding(quantity), {
      shouldValidate: true,
    });
    recalculateAmount();
  };

  return (
    <tr>
      <td>
        <div className="flex-row">
          <div className="w-192">
            {!currentCharge?.custom_charge_code_id ? (
              <InputField
                required={!!currentCharge?.amount || !!currentCharge?.quantity}
                validations={{ validate: { valueExist: isValueExist } }}
                field_id={`costs.${chargeName}.${currentIndex}.description`}
                placeholder="Enter value..."
              />
            ) : (
              <span className={isChargeCodeMandatory ? "required" : ""}>{currentCharge?.description}</span>
            )}
          </div>
        </div>
      </td>
      {chargeName === "other_charges" ? (
        <td>
          <Dropdown
            required={isChargeCodeMandatory}
            useHookForm
            disabled={currentCharge?.custom_charge_code_id}
            name={`costs.${chargeName}.${currentIndex}.applies_to`}
            className="input-md"
            options={[
              {
                label: "Origin",
                value: "origin",
              },
              {
                label: "Destination",
                value: "destination",
              },
            ]}
          />
        </td>
      ) : (
        <td />
      )}
      <td>
        <div className="cost-form-measurement-unit">
          <Dropdown
            required={isChargeCodeMandatory}
            useHookForm
            customLabel={({ value, isActive, hasError }: any) => (
              <input
                type="text"
                className={`font-b3 form-control bg-white br-10 pt-6 pb-6 pr-20 w-100 height-auto placeholder-light ${
                  isActive ? "search-select-input" : "dropdown-hover"
                } ${hasError ? "invalid-input" : ""}`}
                value={cachedConfig?.measurement_units?.find((item: any) => item.key === value)?.label || value || ""}
                readOnly
              />
            )}
            name={`costs.${chargeName}.${currentIndex}.measurement_unit`}
            className="input-md"
            options={options || []}
            onChange={handleMeasurementUnitChange}
          />
        </div>
      </td>
      <td>
        <div className="flex-row input-sm">
          <InputField
            required={isChargeCodeMandatory}
            placeholder="Type here..."
            field_id={`costs.${chargeName}.${currentIndex}.amount`}
            validations={{
              onChange: () => {
                recalculateAmount();
              },
              pattern: {
                value: COST_REGEX,
                message: intl.formatMessage({ id: "general.invalidCost", defaultMessage: "Invalid cost" }),
              },
            }}
            onKeyPress={(event: any) => handleOnKeyPressCost(event)}
          />
        </div>
      </td>
      <td>
        <div className="flex-row input-sm">
          <InputField
            required={isChargeCodeMandatory}
            placeholder="Type here..."
            field_id={`costs.${chargeName}.${currentIndex}.quantity`}
            validations={{
              onChange: () => {
                recalculateAmount();
              },
            }}
            validationPattern={{ value: COST_REGEX, message: "Invalid quantity" }}
            onKeyPress={(event: any) => handleOnKeyPressCost(event)}
          />
        </div>
      </td>
      <td>
        <div className="input-md">
          <SearchSelect
            required={isChargeCodeMandatory}
            name={`costs.${chargeName}.${currentIndex}.currency_code`}
            options={currencies}
            filterBy={["code", "name"]}
            placeholder={intl.formatMessage({ id: "general.currency", defaultMessage: "Currency" })}
            handleRenderOption={handleCurrencyRenderOption}
            onSelect={(event: any) => {
              if (event?.[0]?.value) recalculateAmount();
            }}
          />
        </div>
      </td>
      <td>
        <div className="d-flex align-items-center">
          <span className="text-primary">
            {formatPriceV2(+(currentCharge?.amount || 0) * +(currentCharge?.quantity || 0), {
              currency: currentCharge?.currency_code,
            })}
          </span>
          {!currentCharge?.custom_charge_code_id && (
            <div className="ml-2 cost-remove-btn">
              <CircleCross
                stroke="black"
                onClick={() => {
                  remove(currentIndex);
                  recalculateAmount();
                }}
              />
            </div>
          )}
        </div>
      </td>
    </tr>
  );
}

export default CostFormChargeItem;
